import { Component, HostListener, Input, OnDestroy } from '@angular/core';
import {
	ComponentBase,
	DeploymentContext,
	SearchService,
	WebAnalyticsService,
} from '@Common';
import { Company, SortDetails, SortType } from 'company-finder-common';

@Component({
	selector: 'sort-control',
	templateUrl: './sort-control.component.html',
	styleUrls: ['./sort-control.component.scss'],
	standalone: false,
})
export class SortControlComponent extends ComponentBase implements OnDestroy {
	@Input()
	public companies: Company[];
	public showMenu = false;
	private readonly SortTypes = Object.values(SortType);
	private readonly SortTypesWithoutRanking = this.SortTypes.filter(
		(value) => value !== SortType.Ranking
	);

	public constructor(
		dc: DeploymentContext,
		private _searchService: SearchService,
		private _webAnalyticsService: WebAnalyticsService
	) {
		super(dc);
	}

	public get sortTypes(): SortType[] {
		const types =
			this._searchService.currentSearchPredicate?.trim().length > 0
				? this.SortTypes
				: this.SortTypesWithoutRanking;

		return !this._searchService.companies?.some(
			(company) => !company.commencementDate
		)
			? types
			: types.filter((value) => value !== SortType.Date);
	}

	public get showOptions(): boolean {
		return this.sortTypes.length > 1 && this.companies?.length > 0;
	}

	public get sortByLabel(): string {
		return this.Loc(this.LocalizedTextIds.SortControlSortBy);
	}

	public ngOnDestroy(): void {
		if (this._subscriptions) {
			this._subscriptions.unsubscribe();
			this._subscriptions = undefined;
		}
	}

	public get sortType(): SortType {
		return this._searchService.filter?.sort?.type ?? SortType.Ranking;
	}

	@HostListener('window:click', ['$event'])
	protected onClick(_event: Event): void {
		this.showMenu = false;
	}

	public setShowMenu(ev: MouseEvent): void {
		ev.preventDefault();
		ev.stopPropagation();

		this.showMenu = true;
	}

	public async sort(sortType: SortType): Promise<void> {
		switch (sortType) {
			case SortType.Date:
				this._searchService.filter.sort = SortDetails.DateSort;
				break;
			case SortType.Alpha:
				this._searchService.filter.sort = SortDetails.AlphaSort;
				break;
			case SortType.Ranking:
				this._searchService.filter.sort = SortDetails.RankedSort;
				break;
		}

		this._searchService.drilldownSubject.next(this.mode);

		this._webAnalyticsService.trackEvent('sort-company-search-results', {
			label: sortType,
			// FUTURE: This feels a bit fragile, but if we don't have a count yet from a previous search,
			// assume we are working off the full set
			value:
				this._searchService.companyCountFromLastSearch ||
				this._deploymentContext.comprehensiveSummary.numberOfCompanies,
		});
	}
}
